import { useState } from "react";

/**
 * The shared dashboard date range. Persisted to localStorage so the selection follows the user
 * across the Overview / Usage / Performance sub-pages. `from`/`to` are date-only (yyyy-mm-dd);
 * `preset` distinguishes a quick preset ("7"/"14"/"30") from a hand-picked "custom" window.
 */
export type DashboardPreset = "7" | "14" | "30" | "custom";

export interface DashboardRange {
  from: string; // yyyy-mm-dd (inclusive)
  to: string; // yyyy-mm-dd (inclusive)
  preset: DashboardPreset;
}

const STORAGE_KEY = "dashboardRange";

/**
 * yyyy-mm-dd for a Date, using its **local** calendar day.
 * `toISOString()` would shift the day for any non-UTC timezone (e.g. UTC+3 before 03:00 local
 * resolves to yesterday), which disagrees with the calendar's `max={today(getLocalTimeZone())}`.
 */
function isoDay(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Computes an inclusive [from, to] window of the last `days` days ending today. */
export function presetWindow(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { from: isoDay(from), to: isoDay(to) };
}

/** Preset keys that mean "the last N days ending today" — always recomputed, never replayed. */
const RELATIVE_PRESETS: Record<string, number> = { "7": 7, "14": 14, "30": 30 };

/**
 * Restores the persisted range.
 *
 * A numeric preset is a *rule* ("last 7 days"), not a fixed window: its `from`/`to` are recomputed
 * from today on every load. Persisting the frozen dates instead would leave the selector reading
 * "Last 7 days" while showing a window that ended whenever the app was last opened.
 * Only a "custom" window keeps the exact dates the user picked.
 */
function loadInitial(): DashboardRange {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as DashboardRange;
      const days = RELATIVE_PRESETS[parsed?.preset];
      if (days) return { ...presetWindow(days), preset: parsed.preset };
      if (parsed?.preset === "custom" && parsed?.from && parsed?.to) {
        return { from: parsed.from, to: parsed.to, preset: "custom" };
      }
    }
  } catch {
    // ignore malformed persisted value
  }
  return { ...presetWindow(7), preset: "7" };
}

/**
 * Shared date-range state for all dashboard charts. Every sub-page owns one instance and passes
 * `startDate`/`endDate` down to its cards; the value is mirrored to localStorage so navigating
 * between sub-pages keeps the same window.
 */
export function useDashboardRange() {
  const [range, setRangeState] = useState<DashboardRange>(loadInitial);

  const persist = (next: DashboardRange) => {
    setRangeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore write failures (private mode / quota)
    }
  };

  /** Select a quick preset (7 / 14 / 30 days). */
  const setPreset = (days: number) => {
    persist({ ...presetWindow(days), preset: String(days) as DashboardPreset });
  };

  /** Pick an explicit from/to window from the calendar inputs. */
  const setCustom = (from: string, to: string) => {
    // guard against a reversed range
    const [lo, hi] = from <= to ? [from, to] : [to, from];
    persist({ from: lo, to: hi, preset: "custom" });
  };

  // API params: full-day boundaries so the selected "to" day is included.
  const startDate = `${range.from}T00:00:00.000Z`;
  const endDate = `${range.to}T23:59:59.999Z`;

  // Span in hours — drives hourly vs daily bucketing/labels for the activity series.
  const spanHours = (Date.parse(endDate) - Date.parse(startDate)) / 3_600_000;

  return { range, setPreset, setCustom, startDate, endDate, spanHours };
}
